'use client';

/**
 * Site Configuration Context
 * ==========================
 * Provides site configuration to client components.
 *
 * The config is loaded server-side and passed to the provider,
 * then available to all client components via useSiteConfig() hook.
 *
 * Usage in client components:
 * ```tsx
 * const { companyName, theme, email } = useSiteConfig();
 * ```
 */

import React, { createContext, useContext, ReactNode } from 'react';
import { SiteConfig, DEFAULT_SITE_CONFIG } from '@/lib/config/siteConfig.shared';

// ============================================
// CONTEXT
// ============================================
const SiteConfigContext = createContext<SiteConfig>(DEFAULT_SITE_CONFIG);

// ============================================
// PROVIDER
// ============================================
interface SiteConfigProviderProps {
  children: ReactNode;
  config: SiteConfig;
}

export function SiteConfigProvider({ children, config }: SiteConfigProviderProps) {
  return (
    <SiteConfigContext.Provider value={config}>
      {children}
    </SiteConfigContext.Provider>
  );
}

// ============================================
// HOOK
// ============================================
/**
 * Get site configuration in client components
 *
 * @returns SiteConfig object with all branding settings
 *
 * @example
 * ```tsx
 * function Header() {
 *   const { companyName, logoUrl, theme } = useSiteConfig();
 *   return <img src={logoUrl} alt={companyName} />;
 * }
 * ```
 */
export function useSiteConfig(): SiteConfig {
  const context = useContext(SiteConfigContext);
  if (!context) {
    console.warn('useSiteConfig must be used within a SiteConfigProvider');
    return DEFAULT_SITE_CONFIG;
  }
  return context;
}

// ============================================
// HELPER HOOKS
// ============================================

/**
 * Get company name
 */
export function useCompanyName(): string {
  const { companyName } = useSiteConfig();
  return companyName;
}

/**
 * Get contact info
 */
export function useContactInfo() {
  const config = useSiteConfig();
  return {
    phone: config.phone,
    phoneRaw: config.phoneRaw,
    phoneInternational: config.phoneInternational,
    phoneTel: `tel:+1${config.phoneRaw}`,
    email: config.email,
    address: config.address,
    hours: config.hours,
  };
}

/**
 * Get theme colors
 */
export function useThemeColors() {
  const { theme } = useSiteConfig();
  return theme;
}

/**
 * Get social links
 */
export function useSocialLinks() {
  const { social } = useSiteConfig();
  return social;
}

/**
 * Get feature flags
 */
export function useFeatureFlags() {
  const { features } = useSiteConfig();
  return features;
}

export default SiteConfigContext;
